import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { formatRp } from '@/lib/format';

const AXIS_TICK = { fill: 'var(--fpccs-text-subtle)', fontSize: 11 };
const GRID_STROKE = 'var(--fpccs-chart-grid)';
const COLORS = ['var(--fpccs-primary)', 'var(--fpccs-success)', 'var(--fpccs-info)', 'var(--fpccs-warning)', 'var(--fpccs-danger)', '#8b5cf6'];

function TooltipBox({ active, payload, label, formatter }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-md border border-[var(--fpccs-border-strong)] bg-[var(--fpccs-surface-3)] px-2.5 py-2 shadow-lg">
      <div className="text-xs font-medium mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="text-[11px] flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-muted-fpccs">{p.name}:</span>
          <span className="font-mono text-[var(--fpccs-text)]">{formatter ? formatter(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function WorkspaceCharts({ data }) {
  if (!data) return null;
  const cashflow = data.cash_flow || [];
  const komposisi = data.komposisi || [];
  const monthly = data.pengeluaran_bulanan || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      <div className="card-fpccs p-5 lg:col-span-7" data-testid="chart-cashflow">
        <div className="text-sm font-medium mb-3">Cash Flow Bulanan</div>
        {cashflow.length === 0 ? <EmptyChart /> : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={cashflow} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gInc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--fpccs-success)" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="var(--fpccs-success)" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--fpccs-danger)" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="var(--fpccs-danger)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis dataKey="month" tick={AXIS_TICK} tickLine={false} axisLine={false} />
                <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={(v) => formatRp(v, { compact: true })} width={70} />
                <Tooltip content={<TooltipBox formatter={(v) => formatRp(v)} />} />
                <Legend wrapperStyle={{ fontSize: 11, color: 'var(--fpccs-text-muted)' }} />
                <Area type="monotone" dataKey="income" name="Pemasukan" stroke="var(--fpccs-success)" strokeWidth={2} fill="url(#gInc)" />
                <Area type="monotone" dataKey="expense" name="Pengeluaran" stroke="var(--fpccs-danger)" strokeWidth={2} fill="url(#gExp)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="card-fpccs p-5 lg:col-span-5" data-testid="chart-komposisi">
        <div className="text-sm font-medium mb-3">Komposisi Pengeluaran</div>
        {komposisi.length === 0 ? <EmptyChart /> : (
          <div className="h-72">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={komposisi} dataKey="total" nameKey="category" innerRadius={60} outerRadius={100} paddingAngle={2}>
                  {komposisi.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip content={<TooltipBox formatter={(v) => formatRp(v)} />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="card-fpccs p-5 lg:col-span-12" data-testid="chart-monthly">
        <div className="text-sm font-medium mb-3">Pengeluaran Bulanan</div>
        {monthly.length === 0 ? <EmptyChart /> : (
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={monthly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis dataKey="month" tick={AXIS_TICK} tickLine={false} axisLine={false} />
                <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={(v) => formatRp(v, { compact: true })} width={70} />
                <Tooltip content={<TooltipBox formatter={(v) => formatRp(v)} />} />
                <Bar dataKey="expense" name="Pengeluaran" fill="var(--fpccs-primary)" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="h-64 flex items-center justify-center text-xs text-subtle-fpccs">
      Belum ada data. Tambahkan transaksi untuk melihat grafik.
    </div>
  );
}
